/**
 * License Catalog Page - Admin Portal
 * Manages global license definitions for the platform
 * Control Plane: Does NOT assign licenses to users
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate } from '../components';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Switch } from '../../../components/ui/switch';
import {
  Key,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  DollarSign,
  Link as LinkIcon,
  AlertCircle,
  CheckCircle,
  Package,
  ChevronDown,
  ChevronRight,
  Settings2,
  Archive,
} from 'lucide-react';
import axios from 'axios';
import StatGrid from '../../../components/admin/StatGrid';
import MetricCard from '../../../components/admin/MetricCard';
import EmptyState from '../../../components/admin/EmptyState';
import { Skeleton } from '../../../components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

// Module Key options are NOT hardcoded here -- they're derived from
// availableModules (GET /admin/modules/available), the same
// product_category classification (sourced from PRODUCT_CODES /
// BUNDLED_WITH_CRM / LEGACY_CODES in admin_service.py) that
// ModulesPage.jsx and SubscriptionsPage.jsx already use. Only "sellable"
// products may be picked for a NEW license -- bundled CRM capabilities
// (Schema Builder, App Manager, File Manager, Import/Export Builder,
// Email Templates, AI Features, Features, Connections) and legacy
// products (Field Service) are never independently licensable. See
// getModuleKeyOptions() below.

// Phase 10 (LICENSE_AND_PLAN_FORM_FIELD_AUDIT.md / FINAL_VERIFICATION_AUDIT.md):
// Assignment Type and Visibility Mode were removed from this form entirely --
// exhaustive codebase search found zero runtime readers of either field
// (see the two audit documents). They remain on the backend model and
// existing documents keep whatever value they already have; this form
// simply no longer collects or displays them. Do not reintroduce a
// SelectItem list for either without re-verifying they're still unused.
const BILLING_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_time', label: 'One Time' }
];

const LicenseCatalogPage = () => {
  const { getAdminToken } = useAdminAuth();
  const [licenses, setLicenses] = useState([]);
  const [matchingLicenseTotal, setMatchingLicenseTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [availableModules, setAvailableModules] = useState([]);

  // Phase 10: Assignment Type, Currency, Visibility Mode, Trial Allowed,
  // Trial Days, and Base License are intentionally absent from this state.
  // They remain on the backend model with their existing defaults --
  // omitting them from a create payload lets LicenseCatalogCreate's own
  // field defaults apply; omitting them from an update payload leaves an
  // existing license's stored values untouched (update_license() only
  // $sets keys actually present in the request body).
  const [formData, setFormData] = useState({
    license_code: '',
    license_name: '',
    module_key: '',
    description: '',
    default_price: '',
    billing_frequency: 'monthly',
    sort_order: 0,
    dependencies: [],
    is_active: true
  });

  const fetchLicenses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/license-catalog`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
        params: { search: searchQuery || undefined }
      });
      const rows = response.data.licenses || [];
      setLicenses(rows);
      setMatchingLicenseTotal(response.data.total ?? rows.length);
      setError(null);
    } catch (err) {
      setError('Failed to load licenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAdminToken, searchQuery]);

  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/modules/available`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setAvailableModules(response.data.modules || []);
    } catch (err) {
      console.error('Failed to fetch modules:', err);
    }
  }, [getAdminToken]);

  useEffect(() => {
    fetchLicenses();
    fetchModules();
  }, [fetchLicenses, fetchModules]);

  // Module Key options for the dropdown: only the 9 sellable products
  // (product_category === 'sellable', excluding the "crm" legacy alias of
  // "sales_console"). When editing a license whose module_key is a bundled
  // or legacy capability (created before this restriction, or a legacy
  // product), that value is appended so the field still displays and
  // reads correctly -- it is never offered for a *new* license, since it
  // only appears when it already equals the license being edited.
  const getModuleKeyOptions = () => {
    const sellable = availableModules
      .filter((m) => m.product_category === 'sellable' && !m.duplicate_of)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((m) => ({ value: m.api_name, label: m.name }));

    const currentKey = formData.module_key;
    const alreadyListed = sellable.some((m) => m.value === currentKey);
    if (showEditDialog && currentKey && !alreadyListed) {
      const info = availableModules.find((m) => m.api_name === currentKey);
      sellable.push({
        value: currentKey,
        label: info ? `${info.name} (legacy/bundled)` : currentKey
      });
    }
    return sellable;
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/license-catalog`, {
        ...formData,
        default_price: formData.default_price === '' ? null : Number(formData.default_price),
      }, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setShowCreateDialog(false);
      resetForm();
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create license');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      const updateData = {
        ...formData,
        default_price: formData.default_price === '' ? null : Number(formData.default_price),
      };
      delete updateData.license_code; // Can't update code
      delete updateData.module_key; // Can't update module key
      
      await axios.patch(`${API}/license-catalog/${selectedLicense.id}`, updateData, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setShowEditDialog(false);
      setSelectedLicense(null);
      resetForm();
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update license');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (license) => {
    if (!window.confirm(`Deactivate license "${license.license_name}"?`)) return;
    
    try {
      await axios.delete(`${API}/license-catalog/${license.id}`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deactivate license');
    }
  };

  const openEditDialog = (license) => {
    setSelectedLicense(license);
    setFormData({
      license_code: license.license_code,
      license_name: license.license_name,
      module_key: license.module_key,
      description: license.description || '',
      default_price: license.default_price ?? '',
      billing_frequency: license.billing_frequency,
      sort_order: license.sort_order,
      dependencies: license.dependencies || [],
      is_active: license.is_active
    });
    setShowAdvanced(false);
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      license_code: '',
      license_name: '',
      module_key: '',
      description: '',
      default_price: '',
      billing_frequency: 'monthly',
      sort_order: 0,
      dependencies: [],
      is_active: true
    });
    setShowAdvanced(false);
  };

  const handleDependencyToggle = (licenseCode) => {
    setFormData(prev => {
      const deps = prev.dependencies || [];
      if (deps.includes(licenseCode)) {
        return { ...prev, dependencies: deps.filter(d => d !== licenseCode) };
      } else {
        return { ...prev, dependencies: [...deps, licenseCode] };
      }
    });
  };

  // Analytics header: every figure here is derived from the licenses and
  // availableModules already fetched by this page -- no separate stats
  // endpoint exists, and nothing (revenue collected, seat assignments,
  // per-license usage) is fabricated. Those three would need a new
  // platform-wide tenant_licenses aggregate endpoint to show honestly.
  const legacyModuleKeys = new Set(
    availableModules.filter((m) => m.product_category === 'legacy').map((m) => m.api_name)
  );
  const activeLicenseCount = licenses.filter((l) => l.is_active).length;
  const legacyLicenseCount = licenses.filter((l) => legacyModuleKeys.has(l.module_key)).length;
  const dependencyEdgeCount = licenses.reduce((sum, l) => sum + (l.dependencies?.length || 0), 0);
  const pricedActiveLicenses = licenses.filter(
    (l) => l.is_active && l.default_price !== null && l.default_price !== undefined);
  const unpricedActiveCount = activeLicenseCount - pricedActiveLicenses.length;
  const avgActivePrice = pricedActiveLicenses.length > 0
    ? pricedActiveLicenses.reduce((s, l) => s + Number(l.default_price), 0) / pricedActiveLicenses.length
    : 0;

  return (
    <div className="p-6 space-y-6" data-testid="license-catalog-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            License Catalog
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage global license definitions. Module keys are restricted to sellable products.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate permission="license:write">
            <Button
              onClick={() => { resetForm(); setShowCreateDialog(true); }}
              className="bg-primary hover:bg-primary/90 gap-2"
              data-testid="create-license-btn"
            >
              <Plus className="h-4 w-4" />
              Create License
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Analytics */}
      {loading && licenses.length === 0 ? (
        <StatGrid cols={5}>
          {Array.from({ length: 5 }).map((_, i) => <MetricCard key={i} loading />)}
        </StatGrid>
      ) : (
        <StatGrid cols={5}>
          <MetricCard label="Licenses (current page)" value={licenses.length} icon={Key} accent="primary"
                      hint="Rows loaded on this page; the catalogue total is stated above the table." />
          <MetricCard label="Active (current page)" value={activeLicenseCount} icon={CheckCircle} accent="success" />
          <MetricCard
            label="Legacy (current page)"
            value={legacyLicenseCount}
            icon={Archive}
            accent="warning"
            hint="Licenses whose Module Key maps to a legacy (no longer sellable) product."
          />
          <MetricCard label="Dependency Links (current page)" value={dependencyEdgeCount} icon={LinkIcon} accent="violet" />
          <MetricCard
            label="Avg. Priced License"
            value={avgActivePrice}
            format={(v) => pricedActiveLicenses.length > 0 ? `$${v.toFixed(2)}` : 'Not priced'}
            icon={DollarSign}
            accent="sky"
            hint={`Mean across ${pricedActiveLicenses.length} active priced licenses; ${unpricedActiveCount} active licenses are not priced and are excluded, not treated as zero.`}
          />
        </StatGrid>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <Card>
        <CardContent className="pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search licenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-licenses-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* License Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            Available Licenses
          </CardTitle>
          <CardDescription data-testid="license-catalogue-page-count">
            Showing {licenses.length} on this page
            {matchingLicenseTotal === null
              ? ' · matching catalogue total unavailable'
              : ` of ${matchingLicenseTotal} matching catalogue rows`}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2 py-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 py-2">
                  <Skeleton className="h-4 flex-1 max-w-[160px]" />
                  <Skeleton className="h-5 w-24 rounded-full" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              ))}
            </div>
          ) : licenses.length === 0 ? (
            <EmptyState
              icon={Key}
              title="No licenses yet"
              description="Create one. Nothing seeds this catalogue — a licence is an entitlement record, and its price is set per product on Bundles & Quotations."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>License</TableHead>
                  <TableHead>Module Key</TableHead>
                  <TableHead>Assignment</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Dependencies</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenses.map((license) => (
                  <TableRow key={license.id} data-testid={`license-row-${license.license_code}`}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{license.license_name}</div>
                        <div className="text-xs text-slate-500">{license.license_code}</div>
                        {license.included_with && (
                          <Badge variant="outline" className="mt-1 text-[10px] text-slate-500">
                            Internal compatibility · included with {license.included_with}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{license.module_key}</Badge>
                    </TableCell>
                    <TableCell className="capitalize">{license.assignment_type.replace('_', ' ')}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {license.default_price === null || license.default_price === undefined ? (
                          <span className="font-medium text-amber-700">Not priced</span>
                        ) : (
                          <>
                            <DollarSign className="h-3 w-3" />
                            {Number(license.default_price).toFixed(2)}/{license.billing_frequency === 'monthly' ? 'mo' : license.billing_frequency === 'yearly' ? 'yr' : 'once'}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {license.dependencies?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {license.dependencies.map(dep => (
                            <Badge key={dep} variant="secondary" className="text-xs">{dep}</Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {license.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGate permission="license:write">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(license)}
                            disabled={!!license.included_with}
                            title={license.included_with
                              ? `Internal compatibility licence; ${license.included_with} owns the commercial offer`
                              : 'Edit licence'}
                            data-testid={`edit-license-${license.license_code}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="license:write">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(license)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-license-${license.license_code}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          setSelectedLicense(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Edit License' : 'Create New License'}</DialogTitle>
            <DialogDescription>
              {showEditDialog ? 'Update license configuration' : 'Define a new license type for the platform'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>License Code *</Label>
                <Input
                  value={formData.license_code}
                  onChange={(e) => setFormData({ ...formData, license_code: e.target.value.toUpperCase().replace(/\s/g, '_') })}
                  placeholder="CRM_CORE_SEAT"
                  disabled={showEditDialog}
                  data-testid="license-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label>License Name *</Label>
                <Input
                  value={formData.license_name}
                  onChange={(e) => setFormData({ ...formData, license_name: e.target.value })}
                  placeholder="CRM Core Seat"
                  data-testid="license-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Module Key *</Label>
              <Select
                value={formData.module_key}
                onValueChange={(v) => setFormData({ ...formData, module_key: v })}
                disabled={showEditDialog}
              >
                <SelectTrigger data-testid="module-key-select">
                  <SelectValue placeholder="Select module..." />
                </SelectTrigger>
                <SelectContent>
                  {getModuleKeyOptions().map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="License description..."
              />
            </div>

            <div className="space-y-2">
              <Label>Default Price</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.default_price}
                  onChange={(e) => setFormData({
                    ...formData,
                    default_price: e.target.value === '' ? '' : Number(e.target.value),
                  })}
                  className="pl-9"
                  data-testid="default-price-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Leave blank for Not priced. Enter 0 only when zero is the intended price.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-slate-500">License available for assignment</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            {/* Dependencies */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Dependencies
              </Label>
              <p className="text-xs text-slate-500">Select licenses that must be assigned before this one</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {licenses.filter(l => l.license_code !== formData.license_code).map(license => (
                  <Badge
                    key={license.license_code}
                    variant={formData.dependencies?.includes(license.license_code) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => handleDependencyToggle(license.license_code)}
                  >
                    {formData.dependencies?.includes(license.license_code) && (
                      <CheckCircle className="h-3 w-3 mr-1" />
                    )}
                    {license.license_code}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Advanced Settings */}
            <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2 px-0 text-slate-600 hover:bg-transparent hover:text-slate-900"
                  data-testid="license-advanced-toggle"
                >
                  {showAdvanced ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <Settings2 className="h-4 w-4" />
                  Advanced Settings
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-3">
                <div className="space-y-2">
                  <Label>Billing Frequency</Label>
                  <Select
                    value={formData.billing_frequency}
                    onValueChange={(v) => setFormData({ ...formData, billing_frequency: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_FREQUENCIES.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Reserved for future billing enhancements.</p>
                </div>
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-slate-500">Controls display order in the license list.</p>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setShowEditDialog(false);
              setSelectedLicense(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button
              onClick={showEditDialog ? handleUpdate : handleCreate}
              disabled={saving || !formData.license_code || !formData.license_name || !formData.module_key}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-license-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {showEditDialog ? 'Update License' : 'Create License'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LicenseCatalogPage;
